import { ProtectedImg, ProtectedVideo, MediaError } from "../components/ProtectedMedia";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Truck, Download, Lock, Expand } from "lucide-react";
import { api, abs, money, SHIPPING_LABELS, COUNTRIES } from "../lib/api";
import { useCart } from "../context/AppContext";
import { TypeBadge } from "../components/ProductCard";
import { AudioPlayer } from "../components/AudioPlayer";
import { Loading } from "../components/PageHeader";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

function VideoPreview({ p, img }) {
  const [failed, setFailed] = useState(false);
  return (
    <div>
      <div className="relative">
        <ProtectedVideo controls playsInline preload="metadata" poster={img} mp4={abs(p.preview_url)} webm={abs(p.preview_webm_url)} onLoadError={() => setFailed(true)} className="w-full aspect-video bg-black" data-testid="product-video-preview" />
        {failed && <MediaError testid="product-video-error" />}
      </div>
      <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] muted">30-second public preview · full film {p.duration}</p>
    </div>
  );
}

function Media({ p }) {
  const [zoom, setZoom] = useState(false);
  const img = abs(p.image_urls[0]);
  if (p.type === "video") return <VideoPreview p={p} img={img} />;
  return (
    <div className="space-y-6">
      <button onClick={() => setZoom(true)} className="group art-frame block w-full cursor-zoom-in" data-testid="product-image-zoom-button">
        <ProtectedImg src={img} alt={p.title} className="w-full h-auto max-h-[78vh] object-contain mx-auto" data-testid="product-main-image" />
        <span className="absolute right-4 bottom-4 p-2 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"><Expand size={16} /></span>
      </button>
      {p.type === "audio" && <AudioPlayer src={abs(p.preview_url)} testid="audio-preview" />}
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-[95vw] w-auto border-none bg-black p-0 rounded-none">
          <DialogTitle className="sr-only">{p.title}</DialogTitle>
          <ProtectedImg src={img} alt={p.title} className="max-h-[92vh] w-auto" data-testid="product-lightbox-image" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShippingEstimate({ p }) {
  const [c, setC] = useState("MY");
  const blocked = p.shipping_mode === "malaysia_only" && c !== "MY";
  const cost = p.shipping_mode === "free" ? 0 : c === "MY" ? p.shipping_my : p.shipping_intl;
  return (
    <div className="wb-card p-5" data-testid="shipping-estimate">
      <div className="flex items-center gap-2 eyebrow"><Truck size={13} /> Shipping</div>
      <p className="mt-3 text-sm muted" data-testid="shipping-mode-label">{SHIPPING_LABELS[p.shipping_mode]?.(p)}</p>
      <div className="mt-4 flex items-center gap-3">
        <select value={c} onChange={(e) => setC(e.target.value)} className="wb-input !py-2 text-xs" data-testid="shipping-country-select">
          {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
        <span className={`shrink-0 font-mono text-sm ${blocked ? "text-red-400" : "gold"}`} data-testid="shipping-estimate-value">
          {blocked ? "Not available" : cost === 0 ? "Free" : money(cost)}
        </span>
      </div>
    </div>
  );
}

function Specs({ p }) {
  const specs = useMemo(
    () => [["Medium", p.medium], ["Dimensions", p.dimensions], ["Duration", p.duration], ["Year", p.year]].filter(([, v]) => v),
    [p.medium, p.dimensions, p.duration, p.year],
  );
  return (
    <dl className="mt-10 grid grid-cols-2 gap-y-4 border-t wb-line pt-8 text-sm">
      {specs.map(([k, v]) => <div key={k} className="contents"><dt className="wb-label !mb-0">{k}</dt><dd>{v}</dd></div>)}
    </dl>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setP(null); api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <div className="wb-container py-32 text-center muted" data-testid="product-not-found">This work could not be found.</div>;
  if (!p) return <Loading />;
  const buyNow = () => { add(p); nav("/checkout"); };
  return (
    <div className="wb-container grid gap-12 py-12 lg:grid-cols-12 lg:gap-16" data-testid="product-detail-page">
      <div className="lg:col-span-7 fade-up"><Media p={p} /></div>
      <div className="lg:col-span-5 lg:sticky lg:top-28 h-fit fade-up" style={{ animationDelay: "120ms" }}>
        <TypeBadge type={p.type} />
        <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]" data-testid="product-title">{p.title}</h1>
        <p className="mt-2 text-sm muted">{p.subtitle}</p>
        <div className="mt-8 flex items-baseline gap-4">
          <span className="font-display text-4xl gold" data-testid="product-price">{money(p.price)}</span>
          <span className="font-mono text-xs muted">USD</span>
        </div>
        {p.type === "physical_art" && (
          <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em]" data-testid="product-stock-status">
            {p.sold_out ? <span className="text-red-400">Sold out</span> : <span className="muted">{p.stock} available · original</span>}
          </div>
        )}
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => add(p)} disabled={p.sold_out} className="btn-gold flex-1" data-testid="add-to-cart-button">
            {p.sold_out ? "Sold out" : "Add to cart"}
          </button>
          <button onClick={buyNow} disabled={p.sold_out} className="btn-ghost flex-1" data-testid="buy-now-button">Buy now</button>
        </div>
        <Specs p={p} />
        <p className="mt-8 text-sm leading-relaxed muted" data-testid="product-description">{p.description}</p>
        <div className="mt-8">
          {p.type === "physical_art" ? <ShippingEstimate p={p} /> : (
            <div className="wb-card p-5 text-sm muted space-y-2" data-testid="digital-delivery-info">
              <div className="flex items-center gap-2"><Download size={14} className="gold" /> Instant secure download after payment (link valid 48h, renewable)</div>
              {p.type !== "digital_art" && <div className="flex items-center gap-2"><Lock size={14} className="gold" /> Full-length streaming in My Library for buyers</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
